import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  collection,
  where,
  getDocs,
  limit,
  orderBy,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { db } from '../../firebase';
import { DEFAULT_AVATAR_KEY } from '../../constants/avatars';

const functions = getFunctions(app, 'europe-west2');

export async function getUserProfile(uid) {
  const mainSnap = await getDoc(doc(db, 'users', uid));
  if (!mainSnap.exists()) return null;

  try {
    const privateSnap = await getDoc(doc(db, 'users', uid, 'private', 'data'));
    const privateData = privateSnap.exists() ? privateSnap.data() : {};
    return { id: mainSnap.id, ...mainSnap.data(), ...privateData };
  } catch (err) {
    if (err.code === 'permission-denied') {
      return { id: mainSnap.id, ...mainSnap.data() };
    }
    throw err;
  }
}

export async function isUsernameTaken(username) {
  const q = query(
    collection(db, 'users'),
    where('usernameLower', '==', username.toLowerCase())
  );
  const snap = await getDocs(q);
  return !snap.empty;
}

export async function createUserProfile(uid, { username, displayName }) {
  // Main document — always readable, never contains stats or avatar
  await setDoc(doc(db, 'users', uid), {
    username,
    usernameLower: username.toLowerCase(),
    displayName,
    createdAt: new Date(),
    profileVisible: false,
  });
  // Private document — visibility-gated; avatarKey only (no stats until first game)
  await setDoc(doc(db, 'users', uid, 'private', 'data'), {
    avatarKey: DEFAULT_AVATAR_KEY,
  });
}

export async function updateUserProfile(uid, data) {
  // Route private-subcollection fields; everything else goes to the main doc
  const { avatarKey, miniGameHighScore, customThemes, savedLists, ...mainData } = data;
  const privateData = {};
  if (avatarKey !== undefined) privateData.avatarKey = avatarKey;
  if (miniGameHighScore !== undefined) privateData.miniGameHighScore = miniGameHighScore;
  if (customThemes !== undefined) privateData.customThemes = customThemes;
  if (savedLists !== undefined) privateData.savedLists = savedLists;
  if (Object.keys(privateData).length > 0) {
    await updateDoc(doc(db, 'users', uid, 'private', 'data'), privateData);
  }
  if (Object.keys(mainData).length > 0) {
    await updateDoc(doc(db, 'users', uid), mainData);
  }
}

export async function getUserProfileByUsername(username) {
  const q = query(
    collection(db, 'users'),
    where('usernameLower', '==', username.toLowerCase()),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  const mainData = { id: d.id, ...d.data() };

  try {
    const privateSnap = await getDoc(doc(db, 'users', d.id, 'private', 'data'));
    const privateData = privateSnap.exists() ? privateSnap.data() : {};
    return { ...mainData, ...privateData };
  } catch (err) {
    if (err.code === 'permission-denied') {
      return mainData;
    }
    throw err;
  }
}

export async function changeUsername(uid, newUsername) {
  await updateDoc(doc(db, 'users', uid), {
    username: newUsername,
    usernameLower: newUsername.toLowerCase(),
  });
}

// Fetch the most recent 20 games for a player, newest first.
export async function getGameHistory(uid) {
  const q = query(
    collection(db, 'users', uid, 'gameHistory'),
    orderBy('completedAt', 'desc'),
    limit(20)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Fetch up to `maxLimit` games for a player — used by the detailed stats page.
export async function getDetailedGameHistory(uid, maxLimit = 100) {
  const q = query(
    collection(db, 'users', uid, 'gameHistory'),
    orderBy('completedAt', 'desc'),
    limit(maxLimit)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Triggers the server-side finalizeGame Cloud Function for the current player.
// Idempotent — safe to call multiple times for the same game.
export async function finalizeGameStats(gameId) {
  const fn = httpsCallable(functions, 'finalizeGame');
  await fn({ gameId });
}

// Renames a game history entry for the given user (gameName is the only client-writable field).
// The gameHistory document must already exist (written by the finalizeGame Cloud Function).
export async function renameGame(uid, gameId, gameName) {
  await updateDoc(doc(db, 'users', uid, 'gameHistory', gameId), { gameName });
}

// Permanently deletes the authenticated user's account and all personal data.
// Anonymises the user's name in other players' game histories, then deletes
// all Firestore documents and the Firebase Auth account.
export async function deleteUserAccount() {
  const fn = httpsCallable(functions, 'deleteAccount');
  await fn({});
}
